import {createAction, createActionGroup, props} from '@ngrx/store';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IGroupAccess, IGroupAccessPerStore, IUser, IUserAccess, IUserAccessToken, IUserAccessTokenURL} from '../models';
import {ApplicationFeatureKeys} from '../../../shared/shared-utils/features/features';
import {IStore} from '../../../shared/shared-models/store/store';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IUserAccessOperationProgress} from '../models/user-access-operation-progress.interface';

//#region GLOBAL
/**
 * Sets a dynamic property and its value on the user access state.
 * This is useful for updating a specific property without mutating the entire user object.
 *
 * @param property - The name of the property to update.
 * @param value - The new value for the specified property.
 */
export const setPropertyOnUserAccessState = createAction(
  '[USER ACCESS][Global] Set Property on User Access State',
  props<{
    property: string;
    value: unknown;
  }>(),
);

/**
 * Clears the user-access-related state from the global store.
 *
 * This action is typically dispatched when a user logs out
 * or when resetting the application state.
 *
 * @constant
 */
export const clearUserAccessState = createAction(
  '[USER ACCESS][Global] Clear User Access State',
);
//#endregion

//#region GET USER ACCESS

/**
 * Initiates the process of retrieving user access permissions.
 *
 * This action is dispatched with the current user object and a flag indicating
 * whether the action is part of a login workflow. The retrieved access data
 * will typically include permissions for stores, features, or other app-specific scopes.
 *
 * @param {IUser} user - The user object for whom access is being requested.
 * @param {boolean} isLoggingIn - Indicates whether the access check is occurring during login.
 */
export const getUserAccess = createAction(
  '[USER ACCESS][Access] Get User Access',
  props<{ user: IUser, isLoggingIn: boolean }>(),
);

/**
 * Action dispatched when user access details are successfully retrieved.
 *
 * @param {Object} props - The payload for the action.
 * @param {IUserAccess} props.userAccess - The access details of the user.
 */
export const getUserAccessSuccess = createAction(
  '[USER ACCESS][Access] Get User Access Success',
  props<{ userAccess: IUserAccess }>(),
);

//#endregion


export const createStaffAccessColleague = createAction(
  '[User Access][Staff Access] Create Colleague',
  props<{ userId: string; editedPermissions: IGroupAccessPerStore }>(),
);

export const createStaffAccessColleagueSuccess = createAction(
  '[User Access][Staff Access] Create Colleague Success',
  props<{ userId: string }>(),
);

export const createStaffAccessColleagueFailure = createAction(
  '[User Access][Staff Access] Create Colleague Failure',
  props<{ error: IGlobalError; userId: string }>(),
);

export const clearLastFailedStaffAccessCreateUserId = createAction(
  '[User Access][Staff Access] Clear Last Failed Create User Id',
);

export const setUserAccessOperationProgress = createAction(
  '[User Access] Set Operation Progress',
  props<{ progress: IUserAccessOperationProgress }>(),
);

export const setPermissionsFailure = createAction(
  '[User Access] Set Permissions Failure',
  props<{ error: IGlobalError; userId?: string }>(),
);


export const showUserAccessModal = createAction(
  '[User Access] Show User Access Modal',
  props<{ modalProps: ISharedModalBasic, editedPermissions?: IGroupAccessPerStore, userId?: string }>(),
);

export const setColleaguesUserAccess = createAction(
  '[User Access] Set Colleagues User Access',
  props<{ userId: string, editedPermissions: IGroupAccessPerStore, merge: boolean, newUser: boolean }>(),
);

export const setColleaguesUserAccessSuccess = createAction(
  '[User Access] Set Colleagues User Access Success',
);

export const setColleaguesUserAccessFailure = createAction(
  '[User Access] Set Colleagues User Access Failure',
  props<{ error: IGlobalError; userId: string }>(),
);

export const getGroupAccessSettings = createAction(
  '[User Access] Get Group Access Settings',
);

export const getGroupAccessSettingsSuccess = createAction(
  '[User Access] Get Group Access Settings Success',
  props<{ groupAccess: IGroupAccess }>(),
);

export const getGroupAccessSettingsFailure = createAction(
  '[User Access] Get Group Access Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const setColleaguesUserAccessForNonAccessibleUser = createAction(
  '[User Access] Set Colleagues User Access For Non Accessible User',
  props<{ userId: string, editedPermissions: IGroupAccessPerStore }>(),
);

export const setColleaguesUserAccessForNonAccessibleUserSuccess = createAction(
  '[User Access] Set Colleagues User Access For Non Accessible User Success',
);

export const setColleaguesUserAccessForNonAccessibleUserFailure = createAction(
  '[User Access] Set Colleagues User Access For Non Accessible User Failure',
  props<{ error: IGlobalError }>(),
);

export const setNewServerAdminUser = createAction(
  '[User Access][Server] Set New Server Admin User',
  props<{ userId: string, editedPermissions: IGroupAccessPerStore }>(),
);

export const setNewServerAdminUserSuccess = createAction(
  '[User Access][Server] Set New Server Admin User Success',
  props<{ userId: string }>(),
);

export const setNewServerAdminUserFailure = createAction(
  '[User Access][Server] Set New Server Admin User Failure',
  props<{ error: IGlobalError }>(),
);


/**
 * Action to initiate the process of getting a user access token.
 * The action includes the application feature for which the access token is being requested.
 *
 * @param {ApplicationFeatureKeys} appFeature - The feature of the application for which the user access token is being requested.
 */
export const getUserAccessToken = createAction(
  '[User Access][User Access Token] Get User Access Token',
  props<{ appFeature: ApplicationFeatureKeys }>(),
);

/**
 * Action dispatched when the user access token is successfully retrieved.
 * The action includes the application feature, the user information, and the retrieved user access token.
 *
 * @param {ApplicationFeatureKeys} appFeature - The feature of the application for which the user access token was retrieved.
 * @param {IUser} user - The user information associated with the access token.
 * @param {IUserAccessToken} userAccessToken - The user access token that was successfully retrieved.
 */
export const getUserAccessTokenSuccess = createAction(
  '[User Access][User Access Token] Get User Access Token Success',
  props<{ appFeature: ApplicationFeatureKeys, userAccessToken: IUserAccessToken }>(),
);

/**
 * Action dispatched when there is a failure to retrieve the user access token.
 * The action includes the application feature and the error information related to the failure.
 *
 * @param {ApplicationFeatureKeys} appFeature - The feature of the application for which the user access token was requested.
 * @param {IGlobalError} error - The error information regarding why the request to retrieve the user access token failed.
 */
export const getUserAccessTokenFailure = createAction(
  '[User Access][User Access Token] Get User Access Token Failure',
  props<{ appFeature: ApplicationFeatureKeys, error: IGlobalError }>(),
);

/**
 * Action to set the user access token in the state.
 * This action updates the state with the user access token associated with a specific application feature.
 * If `userAccessToken` is `null` or `undefined`, it indicates the removal or absence of a token.
 *
 * @param {ApplicationFeatureKeys} appFeature - The feature of the application for which the user access token is being set.
 * @param {IUserAccessToken | IUserAccessTokenURL<unknown> | null | undefined} userAccessToken -
 * The user access token (or URL) to be set in the state.
 * It can be `null` or `undefined` if the token needs to be removed or is unavailable.
 */
export const setUserAccessToken = createAction(
  '[User Access][User Access Token] set User Access Token',
  props<{
    appFeature: ApplicationFeatureKeys | undefined,
    userAccessToken: IUserAccessToken | IUserAccessTokenURL<unknown> | null | undefined
  }>(),
);


export const getUserAccessPerFeature = createAction(
  '[User Access][User Access Per Feature] Get User Access Per Store',
  props<{ accessMap: Record<string, IStore[]> }>(),
);

export const deleteUserAccessAsAdmin = createAction(
  '[User Access][Remove User Access] Delete User Access As Admin',
  props<{ userId: string }>(),
);

//#region [User Access | Settings] Set Default Settings **
export const setUserAccessSettingsDefaults = createActionGroup({
  source: 'User Access | Settings',
  events: {
    ['Set Default Settings']: props<{
      requestId?: string;
    }>(),
  },
});
//#endregion